import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useMembership } from '../../hooks/useMembership';
import { LoginPage } from '../../components/ui/animated-characters-login-page';
import AdBanner from '../../components/Advertisement/AdBanner';

const REMEMBERED_EMAIL_KEY = 'notesplus_remembered_email';

const AuthPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    login,
    register,
    requestRegistrationCode,
    requestPasswordResetCode,
    resetPassword,
    loginWithProvider,
    isAuthenticated,
  } = useAuth();
  const { isFreeUser } = useMembership();
  const { t } = useTranslation('auth');

  const getInitialMode = () => {
    if (location.pathname === '/register') return 'register';
    return 'login';
  };

  const [mode, setMode] = useState(getInitialMode());
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [registerCodeSent, setRegisterCodeSent] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [resetCodeVerified, setResetCodeVerified] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/notes', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    const newMode = getInitialMode();
    setMode(newMode);
    setError('');
    setInfoMessage('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (mode === 'login') {
      const rememberedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY);
      if (rememberedEmail) {
        setEmail(rememberedEmail);
        setRememberMe(true);
      } else {
        setEmail('');
        setRememberMe(false);
      }
    }
  }, [mode]);

  const handleSuccess = (message) => {
    if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('notesplus-show-toast', {
        detail: { type: 'success', title: message, message: '' },
      }));
    }
    setTimeout(() => {
      navigate('/notes', { replace: true });
    }, 300);
  };

  const handleSocialAuth = async (provider) => {
    setIsLoading(true);
    setError('');
    try {
      const result = await loginWithProvider(provider === 'Gmail' ? 'google' : provider);
      if (result?.success && result?.requiresConfirmation) {
        return;
      }
      if (result.success) {
        handleSuccess(t('messages.socialLoginSuccess', { provider }));
      } else {
        setError(result.error || t('messages.socialLoginFailed', { provider }));
      }
    } catch (err) {
      setError(t('messages.genericError', { error: err.message }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleAuthSubmit = async (formData) => {
    const { email: formEmail, password, confirmPassword, verificationCode, name: formName } = formData;
    const normalizedEmail = formEmail.trim().toLowerCase();
    setError('');
    setInfoMessage('');
    setIsLoading(true);

    try {
      if (mode === 'register') {
        const displayName = (formName || '').trim() || normalizedEmail.split('@')[0] || normalizedEmail;

        if (password !== confirmPassword) {
          setError(t('messages.passwordMismatch'));
          setIsLoading(false);
          return;
        }

        if (!registerCodeSent) {
          const codeResult = await requestRegistrationCode({ email: normalizedEmail, name: displayName });
          if (codeResult.success) {
            setRegisterCodeSent(true);
            setInfoMessage(t('messages.codeSent'));
          } else {
            setError(codeResult.error || t('messages.codeSendFailed'));
          }
          setIsLoading(false);
          return;
        }

        const result = await register({
          email: normalizedEmail,
          password,
          name: displayName,
          code: verificationCode.trim(),
        });

        if (result.success) {
          handleSuccess(t('messages.registerSuccess'));
        } else {
          setError(result.error || t('messages.registerFailed'));
        }
      } else if (mode === 'login') {
        const result = await login({ email: normalizedEmail, password });
        if (result.success) {
          if (rememberMe) {
            localStorage.setItem(REMEMBERED_EMAIL_KEY, normalizedEmail);
          } else {
            localStorage.removeItem(REMEMBERED_EMAIL_KEY);
          }
          handleSuccess(t('messages.loginSuccess'));
        } else {
          setError(result.error || t('messages.loginFailed'));
        }
      } else if (mode === 'forgot') {
        if (!resetCodeSent) {
          const result = await requestPasswordResetCode({ email: normalizedEmail });
          if (result.success) {
            setResetCodeSent(true);
            setInfoMessage(t('messages.resetCodeSent'));
          } else {
            setError(result.error || t('messages.codeSendFailed'));
          }
          setIsLoading(false);
          return;
        }

        if (!resetCodeVerified) {
          // Frontend transition to password step
          setResetCodeVerified(true);
          setIsLoading(false);
          return;
        }

        if (password !== confirmPassword) {
          setError(t('messages.passwordMismatch'));
          setIsLoading(false);
          return;
        }

        const result = await resetPassword({
          email: normalizedEmail,
          password,
          code: verificationCode.trim(),
        });

        if (result.success) {
          handleSuccess(t('messages.passwordUpdated'));
        } else {
          setError(result.error || t('messages.passwordResetFailed'));
        }
      }
    } catch (err) {
      setError(t('messages.genericError', { error: err.message }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <LoginPage 
        mode={mode}
        onLogin={handleAuthSubmit}
        onSocialAuth={handleSocialAuth}
        onModeSwitch={(newMode) => {
          setMode(newMode);
          navigate(newMode === 'register' ? '/register' : '/login');
        }}
        onForgotPassword={() => setMode('forgot')}
        initialEmail={email}
        error={error}
        infoMessage={infoMessage}
        isLoading={isLoading}
        rememberMe={rememberMe}
        setRememberMe={setRememberMe}
        registerCodeSent={registerCodeSent}
        resetCodeSent={resetCodeSent}
        resetCodeVerified={resetCodeVerified}
        onCancelVerify={() => {
          setRegisterCodeSent(false);
          setResetCodeSent(false);
          setResetCodeVerified(false);
          setError('');
          setInfoMessage('');
        }}
      />
      
      {isFreeUser && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-lg px-4">
          <AdBanner position="auth" className="rounded-xl shadow-2xl" showCloseButton={true} />
        </div>
      )}
    </>
  );
};

export default AuthPage;
